import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  useQuery,
  QueryHookOptions,
  OperationVariables,
  QueryResult,
  from,
  ApolloLink,
  Operation,
  NextLink,
} from '@apollo/client'
import { DocumentNode } from 'graphql'
import { setContext } from '@apollo/client/link/context'
import axios, { AxiosError } from 'axios'
import fetch from 'isomorphic-unfetch'
import { merge, isEqual } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { PREVIEW_AUTH_HEADER_NAME, SITE_KEY_HEADER_NAME, WEAK_PASSWORD_ERROR_MESSAGE } from '../constants'
import jwt_decode from 'jwt-decode'
import { LooseObject } from '@interflora/ui-components/build/common/props'
import { onError } from '@apollo/client/link/error'
import * as Sentry from '@sentry/nextjs'

const JWT_TOKEN = 'JWT-TOKEN'

const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__'

let ssrAuthToken: string | null = null
let isVisualisation = false

// Feature flags that will be sent on all graphQL requests
const headerFeatureFlags = ['alternate-purchase-flow']

const filterFeatureFlags = (featureFlags = {}) =>
  Object.entries(featureFlags)
    .filter(([key]) => headerFeatureFlags.includes(key))
    .reduce((result, [key, value]) => ({ ...result, [key]: value }), {})

const featureFlagLink = (featureFlags: any) =>
  setContext(async (_, { headers }) => {
    const flags = filterFeatureFlags(featureFlags)
    const featureFlagHeaders = Object.keys(flags).length ? { 'X-Feature-Flags': JSON.stringify(flags) } : {}
    return { headers: { ...headers, ...featureFlagHeaders } }
  })

const experimentLink = setContext(async (_, { activeExperiments = {}, headers }) => {
  const experimentHeaders = Object.keys(activeExperiments).length
    ? { 'X-Experiments': JSON.stringify(activeExperiments) }
    : {}
  return { headers: { ...headers, ...experimentHeaders } }
})
const errorLink = onError(({ graphQLErrors, networkError, forward, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions, path }) => {
      if (message.includes('401')) {
        if (localStorage.getItem(JWT_TOKEN)) {
          localStorage.removeItem(JWT_TOKEN)
          window.location.href = '/sign-in'
        }
      } else {
        /**
         * The API already returns a machine-readable reason in `extensions.code`
         * (see InterfloraGraphQLError.fromError), but reporting only `message`
         * threw it away, so failures like "Unable to add line item to cart"
         * arrived in Sentry with no indication of why.
         *
         * The explicit fingerprint matters as much as the code. Every GraphQL
         * error is reported from this one call site, so Sentry's default
         * grouping buckets unrelated bugs into a single issue whose title is
         * whichever error happened to arrive first. Grouping on the operation
         * and code instead gives one issue per actual fault.
         *
         * Deliberately not attached: operation.variables. They carry customer
         * addresses and contact details, and this is a third-party service.
         */
        const code = String(extensions?.code ?? 'UNKNOWN')
        const operationName = operation.operationName ?? 'anonymous'

        console.log(message)
        Sentry.captureException(new Error(message), {
          fingerprint: ['graphql', operationName, code],
          tags: { graphql_code: code, graphql_operation: operationName },
          extra: { path },
        })
      }
    })
  }

  if (networkError) {
    const ignoredErrors = ['failed to fetch', 'load failed']

    if (networkError?.message && ignoredErrors.some((error) => networkError.message.toLowerCase().includes(error))) {
      console.warn(`Ignoring network error: ${networkError.message}`)
    } else {
      console.log(networkError)
      Sentry.captureException(networkError)
    }
  }

  // Only retry on pure network errors (transient connection failures with no graphQL response).
  // Do NOT retry when the server returned graphQL errors (e.g. 500 "aborted") — retrying
  // server-side failures doubles API load without any chance of success.
  if (!graphQLErrors && networkError) {
    try {
      return forward(operation)
    } catch (e) {
      console.log(e)
      Sentry.captureException(e)
    }
  }
})

const authLink = setContext(async (_, { headers }) => {
  if (!ssrAuthToken && typeof window !== 'undefined') {
    ssrAuthToken = localStorage.getItem(JWT_TOKEN)
  }

  if (ssrAuthToken) {
    try {
      const decodedToken: any = jwt_decode(ssrAuthToken)
      if (!decodedToken?.exp) {
        ssrAuthToken = ''
        console.warn('No `exp` property on token. Decoded token:', decodedToken)
        throw new Error('No `exp` property on token')
      }
      if (new Date(decodedToken.exp * 1000).getTime() <= Date.now()) {
        ssrAuthToken = ''
        console.warn('Token has expired. Decoded token:', decodedToken)
        throw new Error('Token has expired')
      }
    } catch (e) {
      try {
        const response = await axios.post<{ authToken: string }>(
          `${
            typeof window === 'undefined'
              ? process.env.MIDDLEWARE_AUTH_ENDPOINT
              : process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT
          }/refresh`,
          {},
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              [SITE_KEY_HEADER_NAME]: getSiteKey(),
            },
          }
        )
        ssrAuthToken = response.data.authToken
        if (typeof window !== 'undefined') {
          localStorage.setItem(JWT_TOKEN, ssrAuthToken)
        }
      } catch (e) {
        console.log('Failed to refresh jwt token')
        ssrAuthToken = null
      }
    }
  }
  if (!ssrAuthToken) {
    const response = await axios.post<{ authToken: string }>(
      `${
        typeof window === 'undefined'
          ? process.env.MIDDLEWARE_AUTH_ENDPOINT
          : process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT
      }/login/anonymous`,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          [SITE_KEY_HEADER_NAME]: getSiteKey(),
        },
      }
    )
    ssrAuthToken = response.data.authToken
    if (typeof window !== 'undefined') {
      localStorage.setItem(JWT_TOKEN, ssrAuthToken)
    }
  }
  headers.authorization = `Bearer ${ssrAuthToken}`

  if (isVisualisation) {
    headers[PREVIEW_AUTH_HEADER_NAME] =
      typeof window === 'undefined' ? process.env.PREVIEW_AUTH_TOKEN : process.env.NEXT_PUBLIC_PREVIEW_AUTH_TOKEN
  }

  headers[SITE_KEY_HEADER_NAME] = getSiteKey()

  return { headers }
})

const httpLink = new HttpLink({
  uri:
    typeof window === 'undefined'
      ? process.env.MIDDLEWARE_GRAPHQL_ENDPOINT
      : process.env.NEXT_PUBLIC_MIDDLEWARE_GRAPHQL_ENDPOINT,
  credentials: 'include',
  fetch,
})

// Apollo Link to append action param to every GraphQL request
const actionParamLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  // Extract action name from operation name or first field name
  let actionName = operation.operationName

  if (!actionName) {
    const definition = operation.query.definitions[0]
    if (definition?.kind === 'OperationDefinition') {
      const firstSelection = definition.selectionSet?.selections[0]
      if (firstSelection?.kind === 'Field') {
        actionName = firstSelection.name.value
      }
    }
  }

  actionName = actionName || 'unknownAction'

  // Get URI and append action parameter
  const context = operation.getContext()
  const uri =
    context.uri ||
    (globalThis.window === undefined
      ? process.env.MIDDLEWARE_GRAPHQL_ENDPOINT
      : process.env.NEXT_PUBLIC_MIDDLEWARE_GRAPHQL_ENDPOINT)

  if (uri) {
    const url = new URL(uri)
    url.searchParams.set('action', actionName)
    operation.setContext({ uri: url.toString() })
  }

  return forward(operation)
})

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined

// Singleton Apollo client for SSG builds — shared across all getStaticProps calls within the
// same build worker so InMemoryCache is reused and repeated queries (e.g. GET_COMPONENT for
// preloadedComponents) are served from cache rather than hitting the network each time.
let buildApolloClient: ApolloClient<NormalizedCacheObject> | undefined

export function getBuildApolloClient(): ApolloClient<NormalizedCacheObject> {
  if (!buildApolloClient) {
    buildApolloClient = createApolloClient([])
  }
  return buildApolloClient
}

function createApolloClient(featureFlags: any) {
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    // Link chain order matters - each link processes the operation before passing to the next:
    // 1. featureFlagLink: Adds X-Feature-Flags header with enabled feature flags
    // 2. experimentLink: Adds X-Experiments header with active A/B test experiments
    // 3. errorLink: Handles GraphQL errors (401 redirects) and network errors with retries
    // 4. authLink: Retrieves JWT token, refreshes if expired, and adds Authorization header
    // 5. actionParamLink: Appends the action query parameter to the URI for analytics tracking
    //    NOTE: authLink MUST come before actionParamLink so headers are set up before URI is modified.
    //    This ensures authorization headers are preserved when actionParamLink updates the context.
    // 6. httpLink: The HTTP transport layer that executes the actual request
    link: from([featureFlagLink(featureFlags), experimentLink, errorLink, authLink, actionParamLink, httpLink]),
    cache: new InMemoryCache({}),
  })
}

export function initializeApollo(
  initialState: NormalizedCacheObject | null = null,
  useVisualisation = false,
  featureFlags: any = []
) {
  isVisualisation = useVisualisation
  const _apolloClient = apolloClient ?? createApolloClient(featureFlags)

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract()

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray: Array<Record<string, unknown>>, sourceArray: Array<Record<string, unknown>>) => [
        ...sourceArray,
        ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
      ],
    })

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data)
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient

  return _apolloClient
}

export function useApollo(pageProps: any) {
  const state = pageProps[APOLLO_STATE_PROP_NAME]
  const { featureFlags } = pageProps
  const store = useMemo(() => initializeApollo(state, false, featureFlags), [state, featureFlags])
  return store
}

export function signIn(data: LooseObject) {
  const headers: Record<string, string | undefined> = {
    'Content-Type': 'application/json',
    [SITE_KEY_HEADER_NAME]: getSiteKey(),
  }
  if (ssrAuthToken) {
    headers.Authorization = `Bearer ${ssrAuthToken}`
  }
  return axios
    .post(`${process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT}/login/customer`, data, {
      withCredentials: true,
      headers,
    })
    .then((response) => {
      localStorage.setItem(JWT_TOKEN, response.data.authToken)
      ssrAuthToken = response.data.authToken
    })
    .catch((error: Error | AxiosError) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error(
            'We were unable to find an account that matched the details provided. Please check your email address and password.'
          )
        }
      }
      throw new Error('There was a problem signing in. Please try again.')
    })
}

const isWeakPasswordApiError = (error: Error | AxiosError): boolean => {
  if (!axios.isAxiosError(error)) return false
  return error.response?.data?.error === 'WEAK_PASSWORD'
}

export function updatePassword(data: LooseObject) {
  const headers: Record<string, string | undefined> = {
    'Content-Type': 'application/json',
    [SITE_KEY_HEADER_NAME]: getSiteKey(),
  }
  if (ssrAuthToken) {
    headers.Authorization = `Bearer ${ssrAuthToken}`
  }
  return axios
    .post(`${process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT}/login/customer/password`, data, {
      withCredentials: true,
      headers,
    })
    .then((response) => {
      localStorage.setItem(JWT_TOKEN, response.data.authToken)
      ssrAuthToken = response.data.authToken
    })
    .catch((error: Error | AxiosError) => {
      if (axios.isAxiosError(error)) {
        if (isWeakPasswordApiError(error)) {
          const err = new Error(WEAK_PASSWORD_ERROR_MESSAGE)
          ;(err as any).code = 'WEAK_PASSWORD'
          throw err
        }
        if (error.response?.status === 401) {
          throw new Error('We were unable to change your password. Please check your current password.')
        }
      }
      throw new Error('There was a problem signing in. Please try again.')
    })
}

export function register(data: LooseObject) {
  return axios
    .post(`${process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT}/register`, data, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        [SITE_KEY_HEADER_NAME]: getSiteKey(),
      },
    })
    .catch((error: Error | AxiosError) => {
      if (axios.isAxiosError(error)) {
        if (isWeakPasswordApiError(error)) {
          const err = new Error(WEAK_PASSWORD_ERROR_MESSAGE)
          ;(err as any).code = 'WEAK_PASSWORD'
          throw err
        }
        if (error.response?.status === 400) {
          throw new Error('We were unable to register your account. Please sign in if you already have an account.')
        }
      }
      throw new Error('There was a problem registering. Please try again.')
    })
}

/**
 * Small wrapper around `useQuery` so that we can use it imperatively.
 *
 * @see Credit: https://github.com/apollographql/react-apollo/issues/3499#issuecomment-586039082
 *
 * @example
 * const callQuery = useImperativeQuery(query, options)
 * const handleClick = async () => {
 *   const{ data, error } = await callQuery()
 * }
 */
export function useImperativeQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {}
): QueryResult<TData, TVariables>['refetch'] {
  const { refetch } = useQuery<TData, TVariables>(query, {
    ...options,
    fetchPolicy: 'no-cache',
    skip: true,
  })

  const imperativelyCallQuery = useCallback(
    (queryVariables?: Partial<TVariables>) => {
      return refetch(queryVariables)
    },
    [refetch]
  )

  return imperativelyCallQuery
}

/**
 * Get the site key based on the appropriate environment variable.
 */
export function getSiteKey() {
  return typeof window === 'undefined' ? process.env.SITE_KEY : process.env.NEXT_PUBLIC_SITE_KEY
}

/**
 * Confirmation registration
 */
export function confirmationRegistration(data: LooseObject) {
  return axios
    .post(`${process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT}/order-confirmation/register`, data, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${ssrAuthToken}`,
        'Content-Type': 'application/json',
        [SITE_KEY_HEADER_NAME]: getSiteKey(),
      },
    })
    .then((response) => {
      localStorage.setItem(JWT_TOKEN, response.data.authToken)
      ssrAuthToken = response.data.authToken
    })
    .catch((error: Error | AxiosError) => {
      if (axios.isAxiosError(error) && isWeakPasswordApiError(error)) {
        const err = new Error(WEAK_PASSWORD_ERROR_MESSAGE)
        ;(err as any).code = 'WEAK_PASSWORD'
        throw err
      }
      throw new Error('There was a problem registering. Please try again.')
    })
}

/**
 * Confirmation SignIn
 */
export function confirmationSignIn(data: LooseObject) {
  return axios
    .post(`${process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT}/order-confirmation/signIn`, data, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${ssrAuthToken}`,
        'Content-Type': 'application/json',
        [SITE_KEY_HEADER_NAME]: getSiteKey(),
      },
    })
    .then((response) => {
      localStorage.setItem(JWT_TOKEN, response.data.authToken)
      ssrAuthToken = response.data.authToken
    })
    .catch((error: Error | AxiosError) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error(
            'We were unable to find an account that matched the details provided. Please check your email address and password.'
          )
        }
      }
      throw new Error('There was a problem signing in. Please try again.')
    })
}

/**
 * Sign out
 */
export function signOut() {
  const url = `${process.env.NEXT_PUBLIC_MIDDLEWARE_AUTH_ENDPOINT}/logout`

  axios
    .post(url, undefined, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${ssrAuthToken}`,
        'Content-Type': 'application/json',
        [SITE_KEY_HEADER_NAME]: getSiteKey(),
      },
    })
    .catch((error) => {
      console.warn(error)
    })

  // Immediately clear the token
  localStorage.removeItem(JWT_TOKEN)
  localStorage.removeItem('idpDiscountRemoved')
  localStorage.removeItem('idpDiscountName')
  ssrAuthToken = null
}
